import React, { useState } from "react";
import { Upload, message, Button, Table, Progress } from "antd";
import type { UploadProps, UploadFile } from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useFileUploadMutation } from "@/hooks/useFileUploadMutation";

const { Dragger } = Upload;

interface FileUploadStatus {
  uid: string;
  name: string;
  size: number;
  status: "uploading" | "done" | "error";
  percent?: number;
  errorMessage?: string;
}

export default function FileUpload() {
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadStatus[]>([]);

  const updateFileProgress = (uid: string, percent: number) => {
    setUploadedFiles((prev) =>
      prev.map((f) => (f.uid === uid ? { ...f, percent } : f)),
    );
  };

  const { uploadFile } = useFileUploadMutation({
    onSuccess: (uid, fileName) => {
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.uid === uid ? { ...f, status: "done", percent: 100 } : f,
        ),
      );
      message.success(`${fileName} uploaded successfully`);
    },
    onError: (uid, fileName, error) => {
      const errorMsg = error.message || "Upload failed";
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.uid === uid ? { ...f, status: "error", errorMessage: errorMsg } : f,
        ),
      );
      message.error(`${fileName} upload failed`);
    },
  });

  const uploadProps: UploadProps = {
    name: "file",
    multiple: true,
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const uploadFile_ = file as File;
      const uid = (file as UploadFile).uid || `${Date.now()}`;

      // Add file to tracking list
      setUploadedFiles((prev) => [
        ...prev,
        {
          uid,
          name: uploadFile_.name,
          size: uploadFile_.size,
          status: "uploading",
          percent: 0,
        },
      ]);

      // Use the mutation hook to handle the upload
      uploadFile(
        {
          file: uploadFile_,
          uid,
          onProgress: updateFileProgress,
        },
        {
          onSuccess: () => {
            onSuccess?.(null);
          },
          onError: (error) => {
            onError?.(error as Error);
          },
        },
      );
    },
  };

  const removeFile = (uid: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.uid !== uid));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "uploading":
        return <LoadingOutlined className="text-blue-500" />;
      case "done":
        return <CheckCircleOutlined className="text-green-500" />;
      case "error":
        return <CloseCircleOutlined className="text-red-500" />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 80,
      render: (status: string) => getStatusIcon(status),
    },
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 120,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: "Progress",
      dataIndex: "percent",
      key: "percent",
      width: 200,
      render: (percent: number, record: FileUploadStatus) =>
        record.status === "uploading" ? (
          <Progress percent={percent} size="small" />
        ) : record.status === "error" ? (
          <span className="text-red-500 text-sm">{record.errorMessage}</span>
        ) : (
          <span className="text-green-500">Complete</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,

      render: (_: any, record: FileUploadStatus) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeFile(record.uid)}
          className="text-gray-500 hover:text-red-500"
        />
      ),
    },
  ];

  return (
    <div className="w-full h-screen flex flex-col">
      <div className="shrink-0 px-4.5 pt-7.5">
        <div className="flex items-center font-bold text-[18px] mb-5 gap-2">
          <p>File Upload</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4.5 hide-scrollbar">
        <div className="mb-6">
          <Dragger {...uploadProps} className="bg-white rounded-lg">
            <p className="ant-upload-drag-icon">
              <InboxOutlined className="text-[#0E98D0] text-5xl" />
            </p>
            <p className="ant-upload-text text-[16px] text-[#2B4752] font-medium">
              Click or drag files to this area to upload
            </p>
            <p className="ant-upload-hint text-[14px] text-gray-500">
              Support for single or bulk file upload. Any file type is accepted.
            </p>
          </Dragger>
        </div>

        {uploadedFiles.length > 0 && (
          <div className="bg-white rounded-lg p-4">
            <h3 className="text-[16px] font-medium text-[#2B4752] mb-4">
              Uploaded Files ({uploadedFiles.length})
            </h3>
            <Table
              dataSource={uploadedFiles}
              columns={columns}
              rowKey="uid"
              pagination={false}
              size="small"
              className="upload-files-table"
            />
          </div>
        )}
      </div>
    </div>
  );
}
